import React, { useEffect, useState } from 'react';
import { Check, Copy, TrendingUp } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import Exportar from '../components/reportes/Exportar';
import EncabezadoImpresion from '../components/reportes/EncabezadoImpresion';
import { formatMonto, formatFecha, formatFechaHora, hoyCaracas } from '../utils/format';
import BotonAyuda from '../components/BotonAyuda';

// ============================================================
// Movimientos duplicados o inusuales (#123) de la empresa activa:
//   - Posibles duplicados: misma cuenta, tipo, monto y fecha.
//   - Montos inusuales: muy por encima del promedio de su categoría.
// Cada caso tiene "Revisado" y, una vez marcado, no vuelve a salir.
// ============================================================

const PREFIJO = { USD: '$', BS: 'Bs ', BCV: 'BCV$ ' };
const monto = (v, moneda) => `${PREFIJO[moneda] || ''}${formatMonto(v)}`;
const TIPO = { ingreso: 'Ingreso', egreso: 'Egreso' };

function haceDias(n) {
  const d = new Date(`${hoyCaracas()}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
}

function BotonRevisado({ onClick, ocupado }) {
  return (
    <button
      onClick={onClick}
      disabled={ocupado}
      className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-xs text-slate-600 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-300 disabled:opacity-50"
      title="Ya lo revisé, está bien: no volver a mostrar"
    >
      <Check size={14} /> Revisado
    </button>
  );
}

export default function ReporteMovimientosControl() {
  const { empresaActiva, empresaActivaNombre } = useAuth();
  const [desde, setDesde] = useState(haceDias(90));
  const [hasta, setHasta] = useState(hoyCaracas());
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState('');

  useEffect(() => {
    if (!empresaActiva) return;
    setDatos(null);
    setError('');
    api.get('/reportes/movimientos-control', { params: { desde: desde || undefined, hasta: hasta || undefined } })
      .then(({ data }) => setDatos(data))
      .catch((err) => setError(err.response?.data?.error || 'No se pudo cargar el reporte'));
  }, [empresaActiva, desde, hasta]);

  const marcar = async (tipo, clave) => {
    setOcupado(`${tipo}:${clave}`);
    try {
      await api.post('/reportes/movimientos-control/revisado', { tipo, clave });
      setDatos((d) => ({
        ...d,
        duplicados: tipo === 'duplicado' ? d.duplicados.filter((x) => x.clave !== clave) : d.duplicados,
        inusuales: tipo === 'inusual' ? d.inusuales.filter((x) => x.clave !== clave) : d.inusuales
      }));
    } catch (err) {
      alert(err.response?.data?.error || 'No se pudo marcar como revisado');
    } finally {
      setOcupado('');
    }
  };

  const c = datos?.criterio;

  const contenidoExcel = () => ({
    titulo: 'Movimientos duplicados o inusuales',
    detalle: `Del ${formatFecha(desde)} al ${formatFecha(hasta)} · cada monto en la moneda de su cuenta`,
    hojas: [
      {
        nombre: 'Posibles duplicados',
        columnas: [
          { titulo: 'Fecha', tipo: 'fecha' }, { titulo: 'Cuenta', tipo: 'texto' }, { titulo: 'Tipo', tipo: 'texto' },
          { titulo: 'Moneda', tipo: 'texto' }, { titulo: 'Monto', tipo: 'monto' }, { titulo: 'Descripción', tipo: 'texto' },
          { titulo: 'Categoría', tipo: 'texto' }, { titulo: 'Referencia', tipo: 'texto' }, { titulo: 'Registrado por', tipo: 'texto' },
          { titulo: 'Registrado el', tipo: 'texto' }
        ],
        filas: datos.duplicados.flatMap((g) => g.movimientos.map((m) => [
          m.fecha, m.cuenta_nombre, TIPO[m.tipo], m.moneda, Number(m.monto), m.descripcion || '',
          m.categoria_nombre || '', m.referencia || '', m.usuario_nombre || '', formatFechaHora(m.creado_en)
        ]))
      },
      {
        nombre: 'Montos inusuales',
        columnas: [
          { titulo: 'Fecha', tipo: 'fecha' }, { titulo: 'Categoría', tipo: 'texto' }, { titulo: 'Tipo', tipo: 'texto' },
          { titulo: 'Descripción', tipo: 'texto' }, { titulo: 'Cuenta', tipo: 'texto' }, { titulo: 'Moneda', tipo: 'texto' },
          { titulo: 'Monto', tipo: 'monto' }, { titulo: 'Promedio', tipo: 'monto' }, { titulo: 'Veces', tipo: 'monto' },
          { titulo: 'Registrado por', tipo: 'texto' }
        ],
        filas: datos.inusuales.map((m) => [
          m.fecha, m.categoria_nombre, TIPO[m.tipo], m.descripcion || '', m.cuenta_nombre, m.moneda,
          Number(m.monto), Number(m.promedio), Math.round(m.veces * 10) / 10, m.usuario_nombre || ''
        ])
      }
    ]
  });

  return (
    <div className="space-y-6">
      <div className="print:hidden">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <h1 className="text-2xl font-bold text-slate-800">
            Movimientos duplicados o inusuales
            {empresaActivaNombre && <span className="text-slate-400 font-normal"> · {empresaActivaNombre}</span>}
            <BotonAyuda />
          </h1>
          <Exportar excel={contenidoExcel} disabled={!datos} />
        </div>
        <p className="text-sm text-slate-500 mt-1">
          Casos para revisar. Si está bien, márcalo como revisado y no vuelve a salir. Cada monto va en la moneda de su cuenta.
        </p>
      </div>

      <EncabezadoImpresion titulo="Movimientos duplicados o inusuales" detalle={`Del ${formatFecha(desde)} al ${formatFecha(hasta)}`} />

      <div className="bg-white rounded-xl shadow-sm p-4 flex flex-wrap items-end gap-3 text-sm print:hidden">
        <label className="flex flex-col gap-1">
          <span className="text-slate-500">Desde</span>
          <input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="border rounded-md px-3 py-2" />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-slate-500">Hasta</span>
          <input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="border rounded-md px-3 py-2" />
        </label>
      </div>

      {error && <p className="text-red-600 text-sm">{error}</p>}
      {!datos && !error && <p className="text-slate-400 text-sm">Cargando…</p>}

      {datos && (
        <>
          {/* Posibles duplicados */}
          <section className="space-y-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-700 flex items-center gap-2">
                <Copy size={18} className="text-amber-600" /> Posibles duplicados
                <span className="text-sm font-normal text-slate-400">({datos.duplicados.length})</span>
              </h2>
              <p className="text-xs text-slate-400">Dos o más movimientos con la misma cuenta, el mismo tipo, el mismo monto y la misma fecha.</p>
            </div>
            {datos.duplicados.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-6 text-center text-slate-400 text-sm">No hay posibles duplicados en el período.</div>
            ) : datos.duplicados.map((g) => (
              <div key={g.clave} className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-amber-50 border-b border-amber-100 text-sm">
                  <span>
                    <span className="font-semibold">{g.movimientos.length} {TIPO[g.tipo]?.toLowerCase()}s</span>
                    {' de '}<span className="font-semibold tabular-nums">{monto(g.monto, g.moneda)}</span>
                    {' el '}{formatFecha(g.fecha)}{' en '}{g.cuenta_nombre}
                  </span>
                  <span className="print:hidden"><BotonRevisado onClick={() => marcar('duplicado', g.clave)} ocupado={ocupado === `duplicado:${g.clave}`} /></span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-slate-500 text-left">
                      <tr>
                        <th className="p-3">Descripción</th>
                        <th className="p-3">Categoría</th>
                        <th className="p-3">Referencia</th>
                        <th className="p-3">Registrado por</th>
                        <th className="p-3 whitespace-nowrap">Registrado el</th>
                      </tr>
                    </thead>
                    <tbody>
                      {g.movimientos.map((m) => (
                        <tr key={m.id} className="border-t">
                          <td className="p-3">{m.descripcion || <span className="text-slate-300">—</span>}</td>
                          <td className="p-3 text-slate-500">{m.categoria_nombre || '—'}</td>
                          <td className="p-3 text-slate-500">{m.referencia || '—'}</td>
                          <td className="p-3 text-slate-500">{m.usuario_nombre || '—'}</td>
                          <td className="p-3 whitespace-nowrap text-slate-500">{formatFechaHora(m.creado_en)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </section>

          {/* Montos inusuales */}
          <section className="space-y-3">
            <div>
              <h2 className="text-lg font-semibold text-slate-700 flex items-center gap-2">
                <TrendingUp size={18} className="text-red-600" /> Montos inusuales
                <span className="text-sm font-normal text-slate-400">({datos.inusuales.length})</span>
              </h2>
              <p className="text-xs text-slate-400">
                Movimientos de {c.veces_promedio} veces o más el promedio de su categoría (mismo tipo y moneda) en los {c.dias_historial} días
                anteriores. Solo cuando la categoría tiene al menos {c.minimo_historial} movimientos para comparar. No incluye transferencias.
              </p>
            </div>
            {datos.inusuales.length === 0 ? (
              <div className="bg-white rounded-xl shadow-sm p-6 text-center text-slate-400 text-sm">No hay montos inusuales en el período.</div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-500 text-left">
                    <tr>
                      <th className="p-3">Fecha</th>
                      <th className="p-3">Categoría</th>
                      <th className="p-3">Descripción</th>
                      <th className="p-3">Cuenta</th>
                      <th className="p-3 text-right">Monto</th>
                      <th className="p-3 text-right">Promedio</th>
                      <th className="p-3 text-right">Veces</th>
                      <th className="p-3 print:hidden" />
                    </tr>
                  </thead>
                  <tbody>
                    {datos.inusuales.map((m) => (
                      <tr key={m.clave} className="border-t">
                        <td className="p-3 whitespace-nowrap">{formatFecha(m.fecha)}</td>
                        <td className="p-3">
                          {m.categoria_nombre}
                          <div className="text-xs text-slate-400">{TIPO[m.tipo]}</div>
                        </td>
                        <td className="p-3 text-slate-500">
                          {m.descripcion || '—'}
                          {m.usuario_nombre && <div className="text-xs text-slate-400">por {m.usuario_nombre}</div>}
                        </td>
                        <td className="p-3 text-slate-500">{m.cuenta_nombre}</td>
                        <td className="p-3 text-right whitespace-nowrap tabular-nums font-semibold">{monto(m.monto, m.moneda)}</td>
                        <td className="p-3 text-right whitespace-nowrap tabular-nums text-slate-500">{monto(m.promedio, m.moneda)}</td>
                        <td className="p-3 text-right whitespace-nowrap text-red-600 font-semibold">{formatMonto(m.veces, 1)}×</td>
                        <td className="p-3 text-right print:hidden">
                          <BotonRevisado onClick={() => marcar('inusual', m.clave)} ocupado={ocupado === `inusual:${m.clave}`} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
